import type {
    MembershipPaymentData,
    SalesManager,
} from '../../services/useMembershipPaymentsQueryService';
import { isSameSalesManager } from './salesRecordDetail';

export type SalesManagerCorrectionFailureAction = 'dismiss' | 'refresh' | 'retry';

export type SalesManagerCorrectionFailure = {
    title: string;
    message: string;
    action: SalesManagerCorrectionFailureAction;
};

export const SALES_RECORD_REFETCH_TIMEOUT_MS = 30000;

export const toSalesManagerIdentity = (manager: SalesManager): { role: string; userId: string } => ({
    role: manager.role,
    userId: manager.userId,
});

const REFRESH_ERROR_CODES = new Set([
    'SALES_MANAGER_CORRECTION_EXPECTED_MISMATCH',
    'MEMBERSHIP_PAYMENT_SOURCE_CHANGED',
    'SALES_MANAGER_CORRECTION_TRANSFER_STATE_CHANGED',
]);

const isMembershipUsageBusyError = (errorCode?: string): boolean => errorCode === 'MEMBERSHIP_USAGE_BUSY';

// 서버가 일부러 만든 오류(errorCode 가 있는 것)만 문구를 그대로 쓴다. 그 외 500 은 운영에서
// "Internal Server Error", Joi 400 은 영어 검증 문구라 화면 기본 문구가 낫다.
const getServerMessage = (result?: { data?: any } | null): string | undefined => {
    const message = result?.data?.message;
    const hasErrorCode = typeof result?.data?.errorCode === 'string' && !!result.data.errorCode;
    return hasErrorCode && typeof message === 'string' && message.trim() ? message : undefined;
};

export const resolveSalesManagerCorrectionFailure = (
    result?: { status?: number; data?: any; networkError?: boolean } | null,
): SalesManagerCorrectionFailure => {
    const status = Number(result?.status) || 0;
    const errorCode = typeof result?.data?.errorCode === 'string' ? result.data.errorCode : undefined;
    const serverMessage = getServerMessage(result);

    if (status === 409) {
        const shouldRefresh = !!errorCode &&
            (REFRESH_ERROR_CODES.has(errorCode) || isMembershipUsageBusyError(errorCode));
        // 잠금 상실(MEMBERSHIP_USAGE_LEASE_LOST) 등 재시도 가능한 409 는 수강권이 이미 새 판매자로
        // 바뀐 뒤 결제행 도중에 날 수 있다. 닫아 버리면 결제행 일부가 옛 판매자로 조용히 남으므로
        // 다시 시도(목록 재조회 → 재확인/마저 맞추기)로 보낸다.
        if (!shouldRefresh && result?.data?.details?.retryable === true) {
            return {
                title: '변경을 끝까지 확인하지 못했어요',
                message: serverMessage || '중간에 처리가 멈췄을 수 있어요. 최신 매출을 확인한 뒤 다시 진행해 주세요.',
                action: 'retry',
            };
        }
        return {
            title: shouldRefresh ? '최신 매출을 다시 확인해 주세요' : '판매자를 수정할 수 없어요',
            message: serverMessage || (shouldRefresh
                ? '판매자나 수강권 상태가 변경됐어요. 매출 목록을 새로고침한 뒤 다시 확인해 주세요.'
                : '현재 상태에서는 판매자를 수정할 수 없어요.'),
            action: shouldRefresh ? 'refresh' : 'dismiss',
        };
    }
    if (status === 400) {
        return {
            title: '선택한 판매자로 바꿀 수 없어요',
            message: serverMessage || '현재 센터에서 선택할 수 있는 재직 판매자인지 확인해 주세요.',
            action: 'dismiss',
        };
    }
    if (status === 404) {
        return {
            title: '결제 내역을 수정할 수 없어요',
            message: serverMessage || '수정 권한이 없거나 결제 내역을 찾을 수 없어요.',
            action: 'dismiss',
        };
    }
    if (status === 503) {
        return {
            title: '판매자 수정 기능을 잠시 사용할 수 없어요',
            message: serverMessage || '잠시 후 다시 시도해 주세요.',
            action: 'retry',
        };
    }
    if (status === 500) {
        return {
            title: '변경을 끝까지 확인하지 못했어요',
            message: serverMessage || '중간에 처리가 멈췄을 수 있어요. 최신 매출을 확인한 뒤 다시 진행해 주세요.',
            action: 'retry',
        };
    }
    if (status === 502 || status === 504) {
        return {
            title: '서버 응답을 끝까지 받지 못했어요',
            message: serverMessage || '요청 결과를 확인하지 못했어요. 최신 매출을 확인한 뒤 다시 진행해 주세요.',
            action: 'retry',
        };
    }
    if (!status) {
        return {
            title: '서버 응답을 받지 못했어요',
            message: serverMessage || '요청 결과를 확인하지 못했어요. 최신 매출을 확인한 뒤 다시 진행해 주세요.',
            action: 'retry',
        };
    }
    return {
        title: '판매자를 수정하지 못했어요',
        message: serverMessage || '현재 요청을 처리할 수 없어요.',
        action: result?.data?.details?.retryable === true ? 'retry' : 'dismiss',
    };
};

export type SalesManagerRetryResolution =
    | { kind: 'confirm'; currentManager: SalesManager }
    | { kind: 'finish'; currentManager: SalesManager }
    | { kind: 'reselect'; currentManager: SalesManager };

/** 실패한 요청의 X/Y와 재조회한 현재 판매자를 비교해 다음 화면만 결정한다. */
// 옛 데이터에는 role 없이 저장된 판매자가 있다. 서버는 role '' 와 "필드 없음"을 같게 보므로
// 비교 전에 세 값 모두 같은 형태로 맞춘다(한쪽만 맞추면 엉뚱하게 reselect 로 간다).
const normalizeRole = (manager: SalesManager): SalesManager =>
    ({ ...manager, role: (typeof manager?.role === 'string' ? manager.role : '') as SalesManager['role'] });

export const resolveSalesManagerRetry = (
    rawExpectedManager: SalesManager,
    rawTargetManager: SalesManager,
    rawRefreshedManager: SalesManager,
): SalesManagerRetryResolution => {
    const expectedManager = normalizeRole(rawExpectedManager);
    const targetManager = normalizeRole(rawTargetManager);
    const refreshedManager = normalizeRole(rawRefreshedManager);
    // 마저 맞추기 요청이 다시 실패하면 expected와 target이 모두 Y다. Y를 먼저 보지 않으면
    // 일반 재확인으로 돌아가 같은 판매자 제출 방어에 막혀 복구를 이어갈 수 없다.
    if (isSameSalesManager(refreshedManager, targetManager)) {
        return { kind: 'finish', currentManager: refreshedManager };
    }
    if (isSameSalesManager(refreshedManager, expectedManager)) {
        return { kind: 'confirm', currentManager: refreshedManager };
    }
    return { kind: 'reselect', currentManager: refreshedManager };
};

/** 다시 시도 버튼은 PATCH가 아니라 최신 결제 행 조회와 다음 확인 단계 계산만 수행한다. */
export const prepareSalesManagerRetry = async (
    expectedManager: SalesManager,
    targetManager: SalesManager,
    refetchSalesRecord: () => Promise<MembershipPaymentData>,
): Promise<SalesManagerRetryResolution> => {
    // 재조회도 응답이 안 오면 "요청 중"에 갇히므로 시간 제한을 둔다(넘으면 실패로 보고 전송하지 않는다).
    let timer: ReturnType<typeof setTimeout> | undefined;
    let refreshedRecord: MembershipPaymentData;
    try {
        refreshedRecord = await Promise.race([
            refetchSalesRecord(),
            new Promise<never>((_, reject) => {
                timer = setTimeout(
                    () => reject(new Error('REFRESH_SALES_RECORD_TIMEOUT')),
                    SALES_RECORD_REFETCH_TIMEOUT_MS,
                );
            }),
        ]);
    } finally {
        if (timer) clearTimeout(timer);
    }
    const rawManager = refreshedRecord?.salesManager;
    if (!rawManager || typeof rawManager.userId !== 'string') {
        throw new Error('REFRESHED_SALES_MANAGER_MISSING');
    }
    // 옛 데이터에는 role 없이 저장된 판매자가 있다. 서버는 role '' 를 "필드 없음"과 같게 본다.
    const refreshedManager = { ...rawManager, role: (typeof rawManager.role === 'string' ? rawManager.role : '') as SalesManager['role'] };
    return resolveSalesManagerRetry(expectedManager, targetManager, refreshedManager);
};
